import { agentControllerRefSchema } from "../daemon/agent-controller-ref.js";
import { array, discriminatedUnion, enum as enumFactory, literal, null as nullFactory, number, strictObject, string, union } from "../../lazy-zod.js";
import { MAX_TEXT_LENGTH } from "../protocol/constants.js";
import { agentProfileIdSchema } from "../protocol/profile.js";
import { spaceThreadContextSchema } from "../space-mesh/space-thread.js";
//#region src/schemas/runtime/agent-task-coordination.ts
const AGENT_TASK_COORDINATION_SCHEMA_VERSION = 1;
const nonEmptyIdSchema = string().trim().min(1).max(512);
const boundedAgentControllerRefSchema = agentControllerRefSchema.pipe(string().max(512));
const nullableNonEmptyIdSchema = union([nonEmptyIdSchema, nullFactory()]);
const nullableReasonCodeSchema = union([string().trim().min(1).max(160), nullFactory()]);
const agentTaskHandleBaseShape = { v: literal(1) };
const spaceWakeAgentTaskHandleSchema = strictObject({
	...agentTaskHandleBaseShape,
	kind: literal("space_wake"),
	sourceClientMessageId: nonEmptyIdSchema,
	spaceId: nonEmptyIdSchema,
	targetProfileId: agentProfileIdSchema
});
const controllerAgentTaskHandleSchema = strictObject({
	...agentTaskHandleBaseShape,
	agentControllerRef: boundedAgentControllerRefSchema,
	hostId: nonEmptyIdSchema,
	kind: literal("controller_task"),
	taskId: nonEmptyIdSchema
});
const agentTaskHandleSchema = discriminatedUnion("kind", [
	spaceWakeAgentTaskHandleSchema,
	strictObject({
		...agentTaskHandleBaseShape,
		deliveryId: nonEmptyIdSchema,
		kind: literal("native_delivery")
	}),
	controllerAgentTaskHandleSchema
]);
const agentTaskTerminalStatusSchema = enumFactory([
	"succeeded",
	"completed",
	"failed",
	"stopped",
	"stayed_silent",
	"publication_failed"
]);
const agentTaskTerminalOutcomeSchema = strictObject({
	errorCode: nullableReasonCodeSchema,
	eventId: nonEmptyIdSchema,
	occurredAtMs: number().int().nonnegative(),
	publishedSignalId: nullableNonEmptyIdSchema,
	status: agentTaskTerminalStatusSchema
});
function areAgentTaskTerminalOutcomesEqual(left, right) {
	return left.errorCode === right.errorCode && left.eventId === right.eventId && left.occurredAtMs === right.occurredAtMs && left.publishedSignalId === right.publishedSignalId && left.status === right.status;
}
const agentTaskNeedsAttentionObservationSchema = strictObject({
	attentionCode: nullableReasonCodeSchema,
	eventId: nonEmptyIdSchema,
	occurredAtMs: number().int().nonnegative(),
	status: literal("needs_attention")
});
const agentTaskCompletionObservationSchema = union([agentTaskTerminalOutcomeSchema, agentTaskNeedsAttentionObservationSchema]);
const agentTaskCompletionConditionSchema = enumFactory([
	"terminal",
	"succeeded",
	"failed",
	"needs_attention"
]);
const agentDelegationBarrierSchema = enumFactory([
	"all_terminal",
	"first_success",
	"on_failure",
	"on_each_terminal"
]);
const legacyAgentTaskContinuationDestinationShape = {
	sourceSignalId: nonEmptyIdSchema,
	spaceId: nonEmptyIdSchema,
	threadContext: union([spaceThreadContextSchema, nullFactory()])
};
const resumeAgentContinuationShape = {
	delegationId: nonEmptyIdSchema,
	instruction: string().trim().min(1).max(4e3),
	kind: literal("resume_agent")
};
const resumeTaskWatchContinuationShape = {
	instruction: string().trim().min(1).max(4e3),
	kind: literal("resume_task_watch"),
	watchId: nonEmptyIdSchema
};
const agentTaskContinuationSchema = union([
	strictObject(resumeAgentContinuationShape),
	strictObject({
		...resumeAgentContinuationShape,
		...legacyAgentTaskContinuationDestinationShape
	}),
	strictObject({
		agentProfileId: agentProfileIdSchema,
		delegationId: nonEmptyIdSchema,
		kind: literal("activate_fallback"),
		spaceId: nonEmptyIdSchema,
		text: string().trim().min(1).max(MAX_TEXT_LENGTH)
	}),
	strictObject(resumeTaskWatchContinuationShape),
	strictObject({
		...resumeTaskWatchContinuationShape,
		...legacyAgentTaskContinuationDestinationShape
	})
]);
const agentDelegationCompletionPolicySchema = discriminatedUnion("mode", [strictObject({ mode: literal("child_reports_directly") }), strictObject({
	mode: literal("resume_parent"),
	when: agentDelegationBarrierSchema
})]);
const agentDelegationChildSnapshotSchema = strictObject({
	agentProfileId: agentProfileIdSchema,
	role: enumFactory(["primary", "fallback"]),
	state: enumFactory([
		"planned",
		"active",
		"terminal",
		"skipped",
		"cancelled"
	]),
	task: agentTaskHandleSchema,
	terminalOutcome: union([agentTaskTerminalOutcomeSchema, nullFactory()])
});
const agentDelegationSnapshotSchema = strictObject({
	barrier: union([agentDelegationBarrierSchema, nullFactory()]),
	children: array(agentDelegationChildSnapshotSchema).min(1).max(26),
	delegationId: nonEmptyIdSchema,
	generation: number().int().positive()
});
function buildAgentTaskKey(handleInput) {
	const handle = agentTaskHandleSchema.parse(handleInput);
	switch (handle.kind) {
		case "space_wake": return [
			handle.kind,
			handle.spaceId,
			handle.sourceClientMessageId,
			handle.targetProfileId
		].map(encodeURIComponent).join(":");
		case "native_delivery": return [handle.kind, handle.deliveryId].map(encodeURIComponent).join(":");
		case "controller_task": return [
			handle.kind,
			handle.agentControllerRef,
			handle.hostId,
			handle.taskId
		].map(encodeURIComponent).join(":");
		default: return assertNever(handle);
	}
}
function assertNever(value) {
	throw new Error(`Unsupported Agent Task Handle: ${String(value)}`);
}
//#endregion
export { AGENT_TASK_COORDINATION_SCHEMA_VERSION, agentDelegationBarrierSchema, agentDelegationChildSnapshotSchema, agentDelegationCompletionPolicySchema, agentDelegationSnapshotSchema, agentTaskCompletionConditionSchema, agentTaskCompletionObservationSchema, agentTaskContinuationSchema, agentTaskHandleSchema, agentTaskNeedsAttentionObservationSchema, agentTaskTerminalOutcomeSchema, agentTaskTerminalStatusSchema, areAgentTaskTerminalOutcomesEqual, buildAgentTaskKey, controllerAgentTaskHandleSchema, spaceWakeAgentTaskHandleSchema };

//# sourceMappingURL=agent-task-coordination.js.map